<template>
  <div class="overflow-auto h-full">
    <div
      class="flex flex-wrap sm:flex-nowrap items-center justify-between my-4 lg:my-8"
    >
      <!-- title -->
      <div class="task-1 flex-grow flex items-center text-navy">
        <svg-icon :path="icons.editTask" :size="32" type="mdi"> </svg-icon>
        <span class="ml-2 font-bold leading-7 text-2xl"> Edit Task </span>
      </div>

      <!-- Back to Task -->
      <div class="task-2 sm:task-3 sm:mr-3">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.task.index')"
        >
          <svg-icon
            type="mdi"
            :path="icons.return"
            :size="20"
            class="text-white"
          ></svg-icon>
          <span class="sr-only"> Task list </span>
        </inertia-link>
      </div>
    </div>

    <flash-message />

    <div class="mt-2 p-2 bg-white shadow text-sm">
      <div class="">
        <h1 class="text-lg font-bold">{{ task.farmer_name }}</h1>

        <div class="mt-1.5">
          {{ task.farmer_address }}
        </div>
      </div>

      <!-- Collection Method Display - read-only after creation -->
      <div class="mt-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Collection Method</label>
        <div class="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
          <span class="text-sm font-medium text-gray-900">
            {{ form.collection_method === 'mobile' ? 'Mobile App Collection' : 'Manual Web Entry' }}
          </span>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          {{ form.collection_method === 'mobile' ? 'This task syncs with the Scout+ mobile app.' : 'Samples are entered directly in the web application.' }}
        </p>
      </div>

      <!-- Lab Selection for Manual Collection -->
      <div class="mt-4" v-if="form.collection_method === 'manual'">
        <h2 class="text-lg font-bold mb-4">Lab Selection</h2>
        
        <div class="sm:w-1/2">
          <label class="block text-sm font-medium text-gray-700" for="manual_lab">
            Select Lab
          </label>

          <select
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="manual_lab"
            v-model="form.lab"
            :disabled="role === 'lab_user'"
          >
            <option value="">Select Lab</option>
            <option v-for="lab in labOptions" :value="lab.id" :key="lab.id" :disabled="lab.disabled">
              {{ lab.label }}
            </option>
          </select>
        </div>

        
      </div>

      <div class="mt-4" v-if="form.collection_method === 'mobile'">
        <div class="flex">
          <h2 class="text-lg font-bold">Lab Tests</h2>

          <div
            class="ml-4 w-7 h-7 text-xl text-center border border-black cursor-pointer"
            v-on:click="openFarmsModal()"
            v-if="form.status == 'not_started' && task.missing_farms.length > 0 && role !== 'lab_user'"
          >
            +
          </div>
        </div>

        <div class="mt-2 sm:w-1/2" v-if="form.status == 'not_started' && form.collection_method === 'mobile'">
          <label class="block text-sm font-medium text-gray-700" for="county">
            Select Lab
          </label>

          <select
            class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            id="lab"
            v-model="form.lab"
            v-on:change="toggleLab()"
            :disabled="role === 'lab_user'"
          >
            <option value="">Select Lab</option>
            <option v-for="lab in labOptions" :value="lab.id" :key="lab.id" :disabled="lab.disabled">
              {{ lab.label }}
            </option>
          </select>
        </div>

        <div class="sm:w-1/2" v-if="form.status != 'not_started' && form.collection_method === 'mobile'">
          <label class="block text-sm font-medium text-gray-700" for="county">
            Selected Lab
          </label>

          <div class="font-bold">
            {{ getSelectedLab }}
          </div>
        </div>

        <div class="mt-2" v-if="form.lab != ''">
          <div
            class="block text-sm font-small text-red-700"
            v-if="form?.errors"
          >
            <p v-for="(message, key) in form?.errors" :key="key">
              {{ message }}
            </p>
          </div>

          <div v-for="(farm, farmKey) in form.tests" :key="farmKey">
            <div class="block text-sm font-medium text-gray-700">
              {{ farm.name }}
            </div>

            <div v-if="form.status == 'not_started'">
              <!-- Show individual test records if available with subtask ID -->
              <div v-if="task.actual_tests && getFarmTests(farm.hashid).length > 0" class="space-y-2">
                <div 
                  v-for="test in getFarmTests(farm.hashid)" 
                  :key="test.hashid"
                  class="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                >
                  <div class="flex items-center space-x-3 flex-grow">
                    <span class="text-sm font-medium">{{ test.subtask_id }}</span>
                    <div class="flex-grow">
                      <Multiselect
                        v-model="form.tests[farmKey].tests"
                        :options="farm_tests"
                        mode="tags"
                        :disabled="role === 'lab_user'"
                      />
                    </div>
                    <div class="w-20">
                      <input
                        class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        type="number"
                        v-model="form.tests[farmKey].no_of_samples"
                        placeholder="Samples"
                        :readonly="role === 'lab_user'"
                      />
                    </div>
                  </div>
                  
                  <button
                    v-if="canCancelFarm(farm) && role !== 'lab_user'"
                    @click="cancelFarmTests(farm, farmKey)"
                    class="ml-2 px-2 py-1 text-xs border rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none disabled:opacity-50"
                    :disabled="updatingStatus === farm.hashid"
                  >
                    {{ updatingStatus === farm.hashid ? 'Removing...' : 'Remove' }}
                  </button>
                </div>
              </div>
              
              <!-- Fallback to original layout if no subtask IDs -->
              <div v-else class="flex gap-x-3 items-center">
                <div class="w-2/3">
                  <div>
                    <Multiselect
                      v-model="form.tests[farmKey].tests"
                      :options="farm_tests"
                      mode="tags"
                      :disabled="role === 'lab_user'"
                    />
                  </div>
                </div>
                <div class="w-1/6">
                  <input
                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    id="po_lab"
                    type="number"
                    v-model="form.tests[farmKey].no_of_samples"
                    placeholder="Samples"
                    :readonly="role === 'lab_user'"
                  />
                </div>
                <div class="w-1/6 flex justify-end">
                  <button
                    v-if="canCancelFarm(farm) && role !== 'lab_user'"
                    @click="cancelFarmTests(farm, farmKey)"
                    class="px-2 py-1 text-xs border rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none disabled:opacity-50"
                    :disabled="updatingStatus === farm.hashid"
                  >
                    {{ updatingStatus === farm.hashid ? 'Removing...' : 'Remove' }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="form.status != 'not_started'">
              <!-- Show individual test records if available, otherwise fall back to test pills -->
              <div v-if="task.actual_tests && getFarmTests(farm.hashid).length > 0" class="space-y-2">
                <div 
                  v-for="test in getFarmTests(farm.hashid)" 
                  :key="test.hashid"
                  class="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                >
                  <div class="flex items-center space-x-3">
                    <span class="text-sm font-medium">{{ test.subtask_id }}</span>
                    <span class="text-sm text-gray-600">
                      <span v-if="test.codes && test.codes.length > 0">
                        {{ test.codes.map(code => farm_tests[code] || code).join(', ') }}
                      </span>
                      <span v-else>No Test Selected</span>
                    </span>
                    <span :class="getStatusClass(test.status)" class="px-2 py-1 text-xs rounded-full">
                      {{ getStatusLabel(test.status) }}
                    </span>
                  </div>
                  
                  <button
                    v-if="canCancelTest(test) && role !== 'lab_user'"
                    @click="cancelTest(test, farmKey)"
                    class="px-3 py-1 text-xs border rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none disabled:opacity-50"
                    :disabled="updatingStatus === test.hashid"
                  >
                    {{ updatingStatus === test.hashid ? 'Cancelling...' : 'Cancel' }}
                  </button>
                </div>
              </div>
              
              <!-- Fallback: show configured tests with names -->
              <div v-else class="space-y-2">
                <div 
                  v-for="(lab_code, key) in form.tests[farmKey].tests"
                  :key="key"
                  class="flex items-center p-2 bg-gray-50 rounded-md"
                >
                  <div class="flex items-center space-x-3">
                    <span class="text-sm font-medium">{{ farm.name }}-{{ key + 1 }}</span>
                    <span class="text-sm text-gray-600">{{ farm_tests[lab_code] || lab_code }}</span>
                    <span class="px-2 py-1 text-xs rounded-full bg-green-200 text-green-800">
                      {{ getStatusLabel(form.status) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <h3 class="text-lg font-bold">Task Details</h3>

        <!-- Status -->
        <div class="sm:w-1/2 mt-2 pr-4">
          <div v-if="statusEditAllowed">
            <label class="block text-sm font-medium text-gray-700" for="status">
              Status
            </label>

            <select
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              id="status"
              v-model="form.status"
            >
              <option
                v-for="(status_name, key) in selectableStatuses"
                :value="key"
                :key="key"
              >
                {{ status_name }}
              </option>
            </select>
          </div>
          <div v-else>
            <label class="block text-sm font-medium text-gray-700" for="status">
              Status
            </label>
            <div class="mt-1 font-semibold">{{ getStatusLabel(form.status) }}</div>
          </div>
        </div>

        <!-- SMAP Survey ID Edit (Admin Only) -->
        <div class="sm:w-1/2 mt-2 pr-4" v-if="role == 'admin' && form.collection_method === 'mobile'">
          <label class="block text-sm font-medium text-gray-700" for="survey_id">
            SMAP Survey ID
          </label>
          <input
            type="text"
            id="survey_id"
            v-model="form.survey_id"
            class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            placeholder="e.g., s14_1066"
            :readonly="role === 'lab_user'"
          />
          <p class="text-xs text-gray-500 mt-1">
            Survey form used for mobile data collection. Changing this will recreate the task on SMAP server.
          </p>
          <div class="text-red-600 text-sm mt-1" v-if="form.errors?.survey_id">
            {{ form.errors.survey_id }}
          </div>
        </div>

        <div class="grid mt-2 sm:grid-cols-2 gap-2 sm:gap-4">
          <div class="">
            <div v-if="role != 'soil_sampler' && form.collection_method !== 'manual'">
              <!-- Assign To -->
              <label for="assigned_to">Assign To</label>

              <button
                type="button"
                id="assigned_to"
                v-on:click="role !== 'lab_user' && (assigned_to.open = !assigned_to.open)"
                class="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'bg-gray-100 cursor-not-allowed': role === 'lab_user' }"
                aria-haspopup="listbox"
                aria-expanded="true"
                aria-labelledby="listbox-label"
                :disabled="role === 'lab_user'"
              >
                <input
                  type="text"
                  class="block w-full truncate p-0 border-none placeholder-gray-300 focus:ring-0"
                  v-model="assigned_to.search"
                  @keyup="searchAssignedTo"
                  :placeholder="assignedToPlaceholderText"
                  :readonly="role === 'lab_user'"
                />

                <!-- Dropdown icon -->
                <span
                  class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  v-show="Object.entries(assigned_to.users).length > 0"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <ul
                class="relative w-[290px] z-10 mt--1 bg-white shadow-lg max-h-60 rounded-b-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                tabindex="-1"
                role="listbox"
                aria-labelledby="listbox-label"
                aria-activedescendant="listbox-option-3"
                v-show="
                  assigned_to.open &&
                  Object.entries(assigned_to.users).length > 0 &&
                  role !== 'lab_user'
                "
              >
                <li
                  class="text-gray-900 hover:bg-gray-100 cursor-pointer select-none relative py-2 px-8"
                  role="option"
                  v-for="(user, index) in assigned_to.users"
                  :key="user.id"
                  v-on:click="selectAssignedTo(index, user)"
                >
                  <span
                    class="font-normal block truncate"
                    :class="
                      assigned_to.chosen == index
                        ? 'font-semibold'
                        : 'font-normal'
                    "
                  >
                    {{ user }}
                  </span>

                  <span
                    class="text-indigo-600 absolute inset-y-0 left-0 flex items-center pl-1.5"
                    v-if="assigned_to.chosen == index"
                  >
                    <svg
                      class="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div class="">
            <div v-if="role == 'admin' && form.collection_method !== 'manual'">
              <!-- Contractor -->
              <label for="contractor">Contractor</label>

              <button
                type="button"
                id="contractor"
                v-on:click="role !== 'lab_user' && (contractor.open = !contractor.open)"
                class="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'bg-gray-100 cursor-not-allowed': role === 'lab_user' }"
                aria-haspopup="listbox"
                aria-expanded="true"
                aria-labelledby="listbox-label"
                :disabled="role === 'lab_user'"
              >
                <input
                  type="text"
                  class="block w-full truncate p-0 border-none placeholder-gray-300 focus:ring-0"
                  v-model="contractor.search"
                  @keyup="searchContractor"
                  :placeholder="contractorPlaceholderText"
                  :readonly="role === 'lab_user'"
                />

                <!-- Dropdown icon -->
                <span
                  class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  v-show="Object.entries(contractor.contractors).length > 0"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <ul
                class="relative w-[290px] z-10 mt--1 bg-white shadow-lg max-h-60 rounded-b-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                tabindex="-1"
                role="listbox"
                aria-labelledby="listbox-label"
                aria-activedescendant="listbox-option-3"
                v-show="
                  contractor.open &&
                  Object.entries(contractor.contractors).length > 0 &&
                  role !== 'lab_user'
                "
              >
                <li
                  class="text-gray-900 hover:bg-gray-100 cursor-pointer select-none relative py-2 px-8"
                  role="option"
                  v-for="(organisation, index) in contractor.contractors"
                  :key="organisation.id"
                  v-on:click="selectContractor(index, organisation)"
                >
                  <span
                    class="font-normal block truncate"
                    :class="
                      assigned_to.chosen == index
                        ? 'font-semibold'
                        : 'font-normal'
                    "
                  >
                    {{ organisation }}
                  </span>

                  <span
                    class="text-indigo-600 absolute inset-y-0 left-0 flex items-center pl-1.5"
                    v-if="contractor.chosen == index"
                  >
                    <svg
                      class="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="mt-2 grid sm:grid-cols-2 gap-2 sm:gap-4">
          <!-- Priority -->
          <div v-if="form.collection_method !== 'manual'">
            <label
              class="block text-sm font-medium text-gray-700"
              for="priority"
            >
              Priority
            </label>

            <select
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              id="priority"
              v-model="form.priority"
              :disabled="role === 'lab_user'"
            >
              <option
                v-for="(priority_name, key) in priorities"
                :value="key"
                :key="key"
              >
                {{ priority_name }}
              </option>
            </select>
          </div>

          <div v-if="form.collection_method !== 'manual'">
            <!-- Active Date -->
            <label
              class="block text-sm font-medium text-gray-700"
              for="active_date"
            >
              Scheduled Date
            </label>

            <VueDatePicker
              range
              :min-date="(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })()"
              :month-change-on-scroll="false"
              name="active_date"
              v-model="form.active_date"
              :text-input="{ format: 'dd/MM/yyyy HH:mm - dd/MM/yyyy HH:mm' }"
              rangeSeparator="-"
              :disabled="role === 'lab_user'"
            />
          </div>
        </div>

        <div class="grid mt-2 sm:grid-cols-2 gap-2 sm:gap-4">
          <div>
            <!-- PO Soil Collection -->
            <label
              class="block text-sm font-medium text-gray-700"
              for="po_soil_collection"
            >
              PO Soil Collection
            </label>

            <input
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              id="po_soil_collection"
              type="text"
              v-model="form.po_soil_collection"
              :readonly="role === 'lab_user'"
            />
          </div>

          <div>
            <!-- PO Lab -->
            <label class="block text-sm font-medium text-gray-700" for="po_lab">
              PO Lab
            </label>

            <input
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              id="po_lab"
              type="text"
              v-model="form.po_lab"
              :readonly="role === 'lab_user'"
            />
          </div>
        </div>

        <div class="mt-2">
          <!-- Notes-->
          <label class="block text-sm font-medium text-gray-700" for="notes">
            Notes/Comments
          </label>

          <textarea
            class="mt-1 w-full focus:ring-indigo-500 focus:border-indigo-500 blockshadow-sm sm:text-sm border-gray-300 rounded-md"
            rows="3"
            name="notes"
            placeholder="Task notes"
            v-model="form.note"
            :readonly="role === 'lab_user'"
          >
          </textarea>
        </div>

        <div class="mt-8 flex justify-between">
          <button
            type="button"
            class="btn-danger"
            @click="showDeleteModal()"
            v-if="['admin', 'consultant', 'consultant_user'].includes(role)"
          >
            Delete Task
          </button>
          <!-- <delete-modal
              :id="'delete-task'"
              :title="'Delete Task'"
              :body="'Are you sure you want to delete this task?'"
            >
            </delete-modal> -->

          <div>
            <button type="submit" class="btn-secondary" @click="cancel">
              Cancel
            </button>
            <button
              type="submit"
              class="btn-primary"
              :disabled="form.collection_method === 'mobile' && (assigned_to.chosen == '' && contractor.chosen == '') && role !== 'lab_user'"
              v-on:click="update"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Farms Modal -->
    <div
      v-if="farmsModal"
      class="absolute top-0 left-0 z-50 w-full h-full bg-black bg-opacity-75 flex items-center justify-center"
    >
      <div class="bg-white rounded w-3/4 py-4 px-10 pb-10 relative">
        <div
          v-on:click="closeFarmsModal()"
          class="absolute top-5 right-5 flex items-center justify-center w-7 h-7 cursor-pointer rounded-full bg-navy hover:bg-navy-dark"
        >
          <svg
            viewBox="0 0 40 40"
            xmlns="http://www.w3.org/2000/svg"
            class="block w-7 h-7 stroke-current text-white"
          >
            <g
              transform="matrix(1.6666666666666667,0,0,1.6666666666666667,0,0)"
            >
              <path
                d="M7.498 16.5L16.497 7.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
              <path
                d="M16.498 16.5L7.497 7.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              ></path>
            </g>
          </svg>

          <span class="sr-only">Close</span>
        </div>

        <div class="mt-14">
          <div
            v-for="(farm, farmKey) in task.missing_farms"
            :key="farmKey"
            class="m-2"
          >
            <label class="flex items-center" :for="'farm_' + farmKey">
              <input
                class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                :id="'farm_' + farmKey"
                type="checkbox"
                v-model="task.missing_farms[farmKey].selected"
              />
              <span class="ml-2 text-sm font-medium text-gray-600">
                {{ farm.lab_data.name }}
              </span>
            </label>
          </div>
        </div>

        <div class="mt-8 flex justify-start">
          <div>
            <span
              class="inline-flex justify-center cursor-pointer py-2 px-4 mr-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              v-on:click="addSelectedFarms()"
            >
              Add Selected Farms
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showDafmLpis } from "@/Partials/Map/Util/DafmLpisPage";
import AppLayout from "@/Layouts/App";
import DeleteModal from "@/Components/DeleteModal";
import { useForm } from "@inertiajs/inertia-vue3";
import _ from "lodash";
// import Datepicker from 'vue3-datepicker'
import parseISO from "date-fns/parseISO";
import Multiselect from "@vueform/multiselect";
import axios from "axios";

import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { mdiArrowLeft, mdiTextBoxEditOutline } from "@mdi/js";
import { goBack } from "@/Composables/Navigation";
import { useToast } from "vue-toastification";
export default {
  components: {
    VueDatePicker,
    DeleteModal,
    Multiselect,
  },
  layout: [AppLayout],
  props: {
    role: String,
    farmer: Object,
    task: Object,
    statuses: Object,
    priorities: Object,
    farms: Array,
    lab_tests: Object,
    labs: Object,
    missing_lab_accounts: { type: Array, default: () => [] },
    lab_account_missing_message: { type: String, default: "" },
  },

  setup() {
    const form = useForm({
      status: "",
      assigned_to_hashid: "",
      contractor_hashid: "",
      priority: "",
      active_date: "",
      po_soil_collection: "",
      po_lab: "",
      note: "",
      lab: "",
      tests: [],
      collection_method: "mobile", // Default to mobile
      survey_id: "", // Custom SMAP survey ID for admin override
    });

    const toast = useToast();

    return { form, toast };
  },

  data() {
    return {
      assigned_to: {
        search: "",
        chosen: "",
        open: false,
        users: {},
      },
      contractor: {
        search: "",
        chosen: "",
        open: false,
        contractors: {},
      },
      farmsModal: false,
      farm_tests: [],
      updatingStatus: null,
      icons: {
        editTask: mdiTextBoxEditOutline,
        return: mdiArrowLeft,
      },
    };
  },

  computed: {
    getSelectedLab() {
      return this.labs.find((lab) => {
        return lab.id == this.form.lab;
      }).name;
    },

    /**
     * Labs for the select. Labs whose organisation account is not set are disabled,
     * except the task's current lab, so the rest of the task can still be edited.
     */
    labOptions() {
      return this.labs.map((lab) => {
        const disabled =
          this.missing_lab_accounts.includes(Number(lab.id)) && Number(lab.id) !== Number(this.task.lab);

        return { id: lab.id, disabled, label: disabled ? `${lab.name} (Account not set up. Contact support@livefarm.ie)` : lab.name };
      });
    },

    assignedToPlaceholderText() {
      if (Object.keys(this.assigned_to.users).length > 0) {
        return "Select Sampler...";
      }

      return "Search Sampler...";
    },

    contractorPlaceholderText() {
      if (Object.keys(this.contractor.contractors).length > 0) {
        return "Select Contractor...";
      }

      return "Search Contractor...";
    },

    isAdmin() {
      return this.role === 'admin';
    },

    // Organisation type check, independent of role
    isLabOrganisation() {
      const pageProps = this.$page?.props || {};
      const auth = pageProps.auth || pageProps;
      const orgType = auth?.user?.organisation?.type || auth?.organisation?.type;
      return orgType?.toLowerCase() === 'lab';
    },

    isLabOrganisationMember() {
      // All members of Lab organisations (including soil samplers)
      return this.isLabOrganisation;
    },

    isLabAdmin() {
      // Users with org admin role in Lab organisations have broader permissions
      return this.isLabOrganisation && this.role === 'consultant';
    },

    isNonLabConsultant() {
      // Consultants in non-lab organisations
      return this.role === 'consultant' && !this.isLabOrganisation;
    },

    statusEditAllowed() {
      if (this.isAdmin) return true;
      if (this.isLabAdmin) return true;
      if (this.isNonLabConsultant) return true;
      if (this.role === 'contractor') return true;
      // All Lab organisation members can see the dropdown (save restrictions still apply)
      return this.isLabOrganisationMember;
    },

    selectableStatuses() {
      // Admins: all statuses
      if (this.isAdmin) {
        return this.statuses;
      }

      // Lab admins: all statuses, but invoiced only for tasks from their own organisation
      if (this.isLabAdmin) {
        const auth = this.$page?.props?.auth || {};
        const userOrgId = auth?.user?.organisation_id;
        if (this.task.organisation_id && userOrgId && this.task.organisation_id !== userOrgId) {
          const filtered = { ...this.statuses };
          delete filtered['invoiced'];
          return filtered;
        }
        return this.statuses;
      }

      // Non-lab consultants: current status + invoiced
      if (this.isNonLabConsultant) {
        const allowed = {};
        if (this.statuses[this.form.status]) allowed[this.form.status] = this.statuses[this.form.status];
        if (this.statuses['invoiced']) allowed['invoiced'] = this.statuses['invoiced'];
        return allowed;
      }

      // Contractors: current status + invoiced (TaskPolicy already scopes edit access to assigned org)
      if (this.role === 'contractor') {
        const allowed = {};
        if (this.statuses[this.form.status]) allowed[this.form.status] = this.statuses[this.form.status];
        if (this.statuses['invoiced']) allowed['invoiced'] = this.statuses['invoiced'];
        return allowed;
      }

      // Lab organisation members: show their allowed options
      if (this.isLabOrganisationMember) {
        if (this.form.status === 'collected' || this.form.status === 'lab' || this.form.status === 'complete') {
          // Can change between collected, lab, and complete
          const allowed = {};
          if (this.statuses['collected']) allowed['collected'] = this.statuses['collected'];
          if (this.statuses['lab']) allowed['lab'] = this.statuses['lab'];
          if (this.statuses['complete']) allowed['complete'] = this.statuses['complete'];
          return allowed;
        } else {
          // For other statuses, show current status only (read-only effectively)
          const current = {};
          if (this.statuses[this.form.status]) {
            current[this.form.status] = this.statuses[this.form.status];
          }
          return current;
        }
      }

      // Otherwise, no selectable options
      return {};
    },
  },

  created() {
    this.form.status = this.task.status;
    this.form.assigned_to_hashid = this.task.assigned_to_hashid;
    this.form.priority = this.task.priority;
    this.form.po_soil_collection = this.task.po_soil_collection;
    this.form.po_lab = this.task.po_lab;
    this.form.note = this.task.note;
    this.form.tests = this.task.tests;
    this.form.lab = this.task.lab;
    this.form.collection_method = this.task.collection_method || 'mobile';
    this.form.survey_id = this.getSurveyId();

    this.assigned_to = {
      search: this.task.assigned_to_name,
      chosen: this.task.assigned_to_hashid,
      open: false,
      users: {},
    };



    this.farm_tests = this.lab_tests;

    const startDate = parseISO(this.task.active_date);
    const endDate = this.task?.complete_by
      ? parseISO(this.task.complete_by)
      : null;
    this.form.active_date = [startDate, endDate];
  },
  mounted() {
    // Enable farm location pins for ALL farmers
    if (this.farmer.farm_location_and_parcels) {
      this.$root.farmer = this.farmer.farm_location_and_parcels;
      this.$root.farmer.count = this.farmer.farms_count;
    }

    if (this.farmer.organisation.name == "ISEC") {
      //load LPIS Map Details
      this.$root.Lpis.source = "isec";
      this.$root.Lpis.herdNo = this.farmer.herd_no;
      this.$root.Lpis.show = true;
      
      // Only show sampler and dropoff locations for ISEC
      this.$root.services.dropoff_points = true;
      this.$root.services.sampler_location = true;
    } else {
      // Linked parcels come with farm_location_and_parcels
      showDafmLpis(this.$root.Lpis);

      this.$root.services.dropoff_points = false;
      this.$root.services.sampler_location = false;
    }
  },

  watch: {
    // Note: only simple paths. Expressions are not supported.
    "form.lab"(newValue) {
      this.farm_tests = this.lab_tests
        .filter((test) => test.lab_id == this.form.lab)
        .reduce((result, test) => {
          result[test.code] = test.name;
          return result;
        }, {});
    },
  },

  methods: {
    getSurveyId() {
      // Get survey ID from the first test or fallback to default
      if (this.task.actual_tests && this.task.actual_tests.length > 0) {
        return this.task.actual_tests[0].survey_ident || 's14_1066';
      }
      return 's14_1066'; // Default survey ID
    },

    canCancelTest(test) {
      // Only show cancel buttons when:
      // 1. The overall task status is "in_progress" AND
      // 2. The individual test status is "not_started" (can't cancel completed tests)
      const testStatus = test.status || 'not_started';
      return this.form.status === 'in_progress' && 
             testStatus === 'not_started' &&
             ['admin', 'consultant', 'consultant_user'].includes(this.role);
    },

    canCancelFarm(farm) {
      // For farms in not_started tasks, allow removing only if:
      // 1. User has permission AND
      // 2. There are more than one farm in the task (to ensure at least one farm remains)
      return ['admin', 'consultant', 'consultant_user'].includes(this.role) && 
             this.form.tests.length > 1;
    },

    getFarmTests(farmHashid) {
      // Get individual test records for a specific farm
      if (!this.task.actual_tests) return [];
      
      return this.task.actual_tests.filter(test => test.farm_hashid === farmHashid);
    },

    getStatusClass(status) {
      const statusClasses = {
        'not_started': 'bg-gray-200 text-gray-800',
        'in_progress': 'bg-blue-200 text-blue-800',
        'collected': 'bg-green-200 text-green-800',
        'rejected': 'bg-red-200 text-red-800',
        'cancelled': 'bg-orange-200 text-orange-800',
        'lab': 'bg-purple-200 text-purple-800',
        'complete': 'bg-green-300 text-green-900',
      };
      return statusClasses[status] || 'bg-gray-200 text-gray-800';
    },

    getStatusLabel(status) {
      return this.statuses[status] || status;
    },

    async cancelTest(test, farmKey) {
      const testName = test.subtask_id || test.name || 'this test';
      
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `Cancel ${testName}? This cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, cancel it!'
      });

      if (!result.isConfirmed) {
        return;
      }

      this.updatingStatus = test.hashid;

      try {
        const response = await axios.patch(route('app.task.test.status', test.hashid), {
          status: 'cancelled'
        });

        if (response.data.success) {
          // Update the test status in the local data
          if (this.task.actual_tests) {
            const testIndex = this.task.actual_tests.findIndex(t => t.hashid === test.hashid);
            if (testIndex !== -1) {
              this.task.actual_tests[testIndex].status = 'cancelled';
            }
          }
          this.toast.success('Test cancelled successfully');
        }
      } catch (error) {
        console.error('Error cancelling test:', error);
        this.toast.error(error.response?.data?.message || 'Failed to cancel test');
      } finally {
        this.updatingStatus = null;
      }
    },

    async cancelFarmTests(farm, farmKey) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `Remove ${farm.name} from this task? Changes will be saved when you click Save.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, remove it!'
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        // Mark tests for removal locally - actual SMAP deletion will happen on save
        if (this.task.actual_tests) {
          const farmTests = this.getFarmTests(farm.hashid);
          
          for (const test of farmTests) {
            // Update local test status to cancelled for UI purposes
            const testIndex = this.task.actual_tests.findIndex(t => t.hashid === test.hashid);
            if (testIndex !== -1) {
              this.task.actual_tests[testIndex].status = 'cancelled';
            }
          }
        }

        // Remove the farm from the tests array locally
        this.form.tests.splice(farmKey, 1);
        this.toast.success('Farm marked for removal - click Save to confirm changes');
      } catch (error) {
        console.error('Error removing farm from task:', error);
        this.toast.error('Failed to remove farm from task');
      }
    },

    toggleLab() {
      // Empty the farms' tests if no lab is selected or
      //  add the default test if a lab is selected
      Object.keys(this.form.tests).forEach((key, index) => {
        if (this.form.lab != "") {
          const defualt_test = this.lab_tests.find((test) => {
            if (
              ((test.pivot && test.pivot.default == 1) ||
                test.is_defualt == 1) &&
              this.form.lab == test.lab_id
            ) {
              return test;
            }
          });
          this.form.tests[key].tests = [defualt_test?.code];
        } else {
          this.form.tests[key].tests = [];
        }
      });
    },

    searchAssignedTo: _.debounce(function () {
      if (this.assigned_to.search.length == 0) {
        this.assigned_to = {
          search: "",
          chosen: "",
          open: "",
          users: {},
        };

        this.contractor = {
          search: "",
          chosen: "",
          open: "",
          contractors: {},
        };
      }

      this.getAssignedTo();
    }, 750),

    getAssignedTo() {
      let search = {
        search: this.assigned_to.search,
        farmer: this.task.farmer_hashid,
      };
      let vm = this;

      axios
        .post(route("app.search-assigned-to", search))
        .then(function (response) {
          let users = response.data.users;

          if (Object.keys(users).length == 1) {
            for (const [index, user] of Object.entries(users)) {
              vm.assigned_to.search = user;
              vm.selectAssignedTo(index, user);
              break;
            }
            vm.assigned_to.users = "";
          } else if (Object.keys(users).length > 1) {
            vm.assigned_to.users = users;
          } else {
            vm.assigned_to.users = "";
          }

          vm.assigned_to.open = false;
        });
    },

    selectAssignedTo(index, user) {
      this.assigned_to.chosen = index;
      this.assigned_to.search = user;
      this.assigned_to.open = false;

      if (this.role == "admin") {
        this.getContractor();
      }
    },

    searchContractor: _.debounce(function () {
      if (this.contractor.search.length == 0) {
        this.contractor = {
          search: "",
          chosen: "",
          open: "",
          contractors: {},
        };
      }

      this.getContractor();
    }, 750),

    getContractor() {
      let search = {
        contractor: this.assigned_to.chosen,
        search: this.contractor.search,
      };

      let vm = this;

      axios
        .post(route("app.search-contractor", search))
        .then(function (response) {
          let contractors = response.data.contractors;

          // Select always the first one (if there's any)
          if (Object.keys(contractors).length > 0) {
            for (const [index, contractor] of Object.entries(contractors)) {
              vm.contractor.search = contractor;
              vm.selectContractor(index, contractor);
              break;
            }

            vm.contractor.contractors = contractors;
          } else {
            vm.contractor.search = "";
            vm.contractor.chosen = "";
            vm.contractor.contractors = "";
          }

          vm.contractor.open = false;
        });
    },

    selectContractor(index, contractor) {
      this.contractor.chosen = index;
      this.contractor.search = contractor;
      this.contractor.open = false;
    },

    openFarmsModal() {
      this.farmsModal = true;
    },

    closeFarmsModal() {
      this.farmsModal = false;
    },

    addSelectedFarms() {
      let unselected = [];

      for (let i = 0; i < this.task.missing_farms.length; i++) {
        if (this.task.missing_farms[i].selected) {
          this.task.tests.push(this.task.missing_farms[i].lab_data);
        } else {
          unselected.push(this.task.missing_farms[i]);
        }
      }

      this.farmsModal = false;
      this.task.missing_farms = JSON.parse(JSON.stringify(unselected));
    },

    update() {
      // Validate lab selection for both mobile and manual tasks
      if (!this.form.lab) {
        this.toast.error("Please select a lab!");
        return;
      }

      // Exclude soil_sampler in Lab organisations from changing status at all
      if (!this.isAdmin && this.isLabOrganisation && this.role === 'soil_sampler') {
        const originalStatus = this.task.status;
        const newStatus = this.form.status;
        if (originalStatus !== newStatus) {
          this.toast.error('Soil samplers in Lab organisations cannot change status');
          return;
        }
      }

      // Allow Lab-organisation members to change between collected, lab, and complete statuses (excluding lab admins)
      if (!this.isAdmin && !this.isLabAdmin && this.isLabOrganisationMember) {
        const originalStatus = this.task.status;
        const newStatus = this.form.status;
        const allowedStatuses = ['collected', 'lab', 'complete'];

        if (!allowedStatuses.includes(originalStatus) || !allowedStatuses.includes(newStatus)) {
          this.toast.error('Users in Lab organisations can only change between Collected, Lab, and Complete statuses');
          return;
        }
      }

      // Only validate tests for mobile collection method (skip for lab organisation members who are just changing status)
      if (this.form.collection_method === 'mobile' && !this.isLabOrganisationMember) {
        // Check if at least one farm has a test selected
        const hasTests = this.form.tests.some(
            farm => Array.isArray(farm.tests) && farm.tests.filter(Boolean).length > 0
        );

        // If no tests are selected, show an error
        if (!hasTests) {
            this.toast.error("Please select at least one test for a farm!");
            return;
        }
      }

      // Only set assignment for mobile collection method
      if (this.form.collection_method === 'mobile') {
        this.form.assigned_to_hashid = this.assigned_to.chosen;
        this.form.contractor_hashid = this.contractor.chosen;
      } else {
        // For manual tasks, clear assignment fields
        this.form.assigned_to_hashid = '';
        this.form.contractor_hashid = '';
      }

      this.form.put(route("app.task.update", this.task.id), {
        onSuccess: () => {
          this.toast.success('Task updated successfully');
        },
        onError: () => {
          this.toast.error('Failed to update task');
        }
      });
    },

    delete() {
      this.form.delete(route("app.task.delete", this.task.id));
      this.$swal.fire("Deleted!", "Task has been deleted.", "success");
    },
    cancel() {
      goBack();
    },
    showDeleteModal() {
      const vm = this;
      this.$swal
        .fire({
          title: "Do you really want to delete this task?",

          showCancelButton: true,
          confirmButtonText: "Delete",
        })
        .then((result) => {
          if (result.isConfirmed) {
            vm.delete();
          }
        });
    },


  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
